import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { Menu, X, ArrowUpRight, LogIn, LogOut, Heart } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useFavourites } from "../lib/favourites";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import ListPropertyForm from "./ListPropertyForm";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/properties", label: "Properties" },
  { to: "/services", label: "Services" },
  { to: "/nri", label: "NRI Hub" },
  { to: "/portal", label: "JV & Distressed" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const currencies = ["INR", "USD", "AED"];

export default function Nav({ currency, setCurrency }) {
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count: favCount } = useFavourites();
  const nav = useNavigate();

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="fixed top-0 inset-x-0 z-50 nav-glass"
        data-testid="main-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/bbe-logo.png" alt="BBE" className="h-12 w-auto object-contain" />
            <div className="leading-none hidden sm:block">
              <div className="font-display text-[15px] tracking-[1px]">Build Brigade</div>
              <div className="text-[8px] tracking-[2.5px] uppercase text-[#D4AF37] mt-[2px]">Enterprises</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 whitespace-nowrap">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/ .*/, "")}`}
                className={({ isActive }) =>
                  `text-[11px] tracking-[2px] uppercase transition-colors whitespace-nowrap ${isActive ? "text-[#D4AF37]" : "text-[#EDE6D3] hover:text-[#D4AF37]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-link-admin"
                className={({ isActive }) =>
                  `text-[11px] tracking-[2px] uppercase transition-colors whitespace-nowrap ${isActive ? "text-[#D4AF37]" : "text-[#7A1C1C] hover:text-[#D4AF37]"}`
                }
              >
                Admin
              </NavLink>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-[11px] tracking-[2px] uppercase border border-[#EDE6D3]/20 px-3 py-2 hover:border-[#D4AF37] transition-colors" data-testid="currency-selector">
                  {currency}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[100px]">
                {currencies.map((c) => (
                  <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}>{c}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/favourites" className="relative p-2 border border-[#EDE6D3]/20 hover:border-[#D4AF37] transition-colors" data-testid="nav-favourites" title="Saved properties">
              <Heart size={16} className={favCount > 0 ? "text-[#D4AF37] fill-[#D4AF37]" : ""} />
              {favCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#D4AF37] text-[#0A2342] text-[9px] font-bold flex items-center justify-center">{favCount}</span>
              )}
            </Link>

            <button className="btn-outline" onClick={() => setListOpen(true)} data-testid="list-property-btn">List Property <ArrowUpRight size={14} /></button>

            {user ? (
              <div className="flex items-center gap-2">
                <button
                  className="btn-primary"
                  onClick={() => nav(user.role === "admin" ? "/admin" : "/portal")}
                  data-testid="investor-desk-btn"
                >{user.role === "admin" ? "Admin Console" : "Investor Desk"} <ArrowUpRight size={14} /></button>
                <button onClick={() => { logout(); nav("/"); }} className="p-2 border border-[#EDE6D3]/20 hover:border-[#D4AF37] transition-colors" data-testid="logout-btn"><LogOut size={14} /></button>
              </div>
            ) : (
              <button className="btn-primary" onClick={() => nav("/login")} data-testid="login-btn"><LogIn size={14} /> Investor Desk</button>
            )}
          </div>

          <button className="lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#EDE6D3]/10 bg-[#0A2342]">
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[13px] tracking-[2px] uppercase">
                  {l.label}
                </NavLink>
              ))}
              {user?.role === "admin" && (
                <NavLink to="/admin" onClick={() => setOpen(false)} className="text-[13px] tracking-[2px] uppercase text-[#7A1C1C]">Admin</NavLink>
              )}
              <div className="flex gap-3 pt-2 flex-wrap">
                <button className="btn-outline" onClick={() => { setListOpen(true); setOpen(false); }}>List Property</button>
                {user
                  ? <button className="btn-primary" onClick={() => { nav(user.role === "admin" ? "/admin" : "/portal"); setOpen(false); }}>{user.role === "admin" ? "Admin" : "Investor Desk"}</button>
                  : <button className="btn-primary" onClick={() => { nav("/login"); setOpen(false); }}>Login</button>}
              </div>
            </div>
          </div>
        )}
      </motion.header>

      <Dialog open={listOpen} onOpenChange={setListOpen}>
        <DialogContent className="max-w-2xl bg-[#0A2342] border-[#EDE6D3]/15 rounded-none">
          <DialogHeader>
            <div className="eyebrow mb-2">Owner Submission</div>
            <DialogTitle className="font-serif text-3xl font-normal">List Your Property</DialogTitle>
            <DialogDescription className="text-[13px] text-[#EDE6D3]/60">
              Our acquisitions team reviews every submission within 48 business hours.
            </DialogDescription>
          </DialogHeader>
          <ListPropertyForm onDone={() => setListOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
